import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Video, Link2, Unlink, CheckCircle2, AlertCircle, Clock, Calendar as CalendarIcon, Trash2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { canEdit as canEditSection, getAdminSession } from '@/lib/auth';
import { useSearchParams } from 'react-router-dom';
import { ADMIN_SETTINGS } from '@/constants/testIds';
import { formatDateTime } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

export default function AdminSettingsPage() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [cancelHrs, setCancelHrs] = useState(12);
    const [rescheduleHrs, setRescheduleHrs] = useState(12);
    const [searchParams, setSearchParams] = useSearchParams();
    const canEdit = canEditSection('settings');
    const isSuperAdmin = getAdminSession()?.admin?.role === 'super_admin';
    const [resetOpen, setResetOpen] = useState(false);
    const [resetConfirm, setResetConfirm] = useState('');
    const [resetting, setResetting] = useState(false);

    const resetBookings = async () => {
        setResetting(true);
        try {
            const { data } = await api.post('/admin/maintenance/reset-bookings');
            const d = data.deleted || {};
            toast.success(`Cleared ${d.bookings || 0} bookings and ${(d.mentor_feedback || 0) + (d.student_feedback || 0)} feedback records.`);
            setResetOpen(false);
            setResetConfirm('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reset failed.');
        } finally {
            setResetting(false);
        }
    };

    useEffect(() => {
        const s = searchParams.get('system_calendar');
        if (s === 'connected') {
            toast.success(`System calendar connected (${searchParams.get('email') || ''})`);
            // clean up URL
            searchParams.delete('system_calendar');
            searchParams.delete('email');
            setSearchParams(searchParams, { replace: true });
        } else if (s === 'error') {
            toast.error(`Failed to connect system calendar: ${searchParams.get('msg') || 'unknown error'}`);
            searchParams.delete('system_calendar');
            searchParams.delete('msg');
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/settings');
            setSettings(data);
            setCancelHrs(data.cancel_cutoff_hours);
            setRescheduleHrs(data.reschedule_cutoff_hours);
        } catch (e) {
            toast.error('Could not load settings.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put('/admin/settings', {
                cancel_cutoff_hours: Number(cancelHrs),
                reschedule_cutoff_hours: Number(rescheduleHrs),
            });
            setSettings(data);
            toast.success('Settings saved.');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    const connectSystemCalendar = async () => {
        try {
            const { data } = await api.get('/admin/system-calendar/oauth-url');
            window.location.href = data.authorization_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not start OAuth.');
        }
    };

    const disconnectSystemCalendar = async () => {
        if (!window.confirm('Disconnect the system Google calendar? New bookings will not automatically get a Meet link unless the mentor has their own calendar connected.')) return;
        try {
            await api.delete('/admin/system-calendar/connection');
            toast.success('System calendar disconnected.');
            load();
        } catch (e) {
            toast.error('Could not disconnect.');
        }
    };

    return (
        <AdminLayout title="Settings" subtitle="Booking policies + system Google calendar">
            {loading ? (
                <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 p-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
            ) : (
                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Booking policies */}
                    <Card>
                        <CardContent className="space-y-5 p-6">
                            <div className="flex items-start gap-3">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                    <Clock className="h-5 w-5" />
                                </div>
                                <div>
                                    <h2 className="font-display text-lg font-semibold">Cancellation & Reschedule policy</h2>
                                    <p className="text-sm text-muted-foreground">Students can only cancel/reschedule if their session is more than the configured hours away.</p>
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <Label htmlFor="cancel-hrs">Cancel cutoff (hours before session)</Label>
                                    <Input
                                        id="cancel-hrs"
                                        type="number"
                                        min="0"
                                        max="720"
                                        value={cancelHrs}
                                        onChange={(e) => setCancelHrs(e.target.value)}
                                        disabled={!canEdit}
                                        data-testid={ADMIN_SETTINGS.cancel_input}
                                        className="mt-1"
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">Inside this window, cancel is blocked.</p>
                                </div>
                                <div>
                                    <Label htmlFor="reschedule-hrs">Reschedule cutoff (hours before session)</Label>
                                    <Input
                                        id="reschedule-hrs"
                                        type="number"
                                        min="0"
                                        max="720"
                                        value={rescheduleHrs}
                                        onChange={(e) => setRescheduleHrs(e.target.value)}
                                        disabled={!canEdit}
                                        data-testid={ADMIN_SETTINGS.reschedule_input}
                                        className="mt-1"
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">Inside this window, reschedule is blocked.</p>
                                </div>
                            </div>

                            {settings?.updated_at && (
                                <p className="text-xs text-muted-foreground">
                                    Last updated {formatDateTime(settings.updated_at)}
                                    {settings.updated_by && <> by {settings.updated_by}</>}
                                </p>
                            )}

                            <div>
                                <Button
                                    onClick={save}
                                    disabled={!canEdit || saving}
                                    data-testid={ADMIN_SETTINGS.save}
                                >
                                    {saving ? (
                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
                                    ) : (
                                        <><Save className="mr-2 h-4 w-4" /> Save changes</>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* System calendar */}
                    <Card>
                        <CardContent className="space-y-5 p-6">
                            <div className="flex items-start gap-3">
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                    <Video className="h-5 w-5" />
                                </div>
                                <div>
                                    <h2 className="font-display text-lg font-semibold">System Google Calendar</h2>
                                    <p className="text-sm text-muted-foreground">Used as a fallback to always attach a Google Meet link to every booking when the mentor has not connected their own calendar.</p>
                                </div>
                            </div>

                            {settings?.system_calendar?.connected ? (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-emerald-900">Connected</div>
                                            <div className="truncate text-xs text-emerald-800">{settings.system_calendar.email}</div>
                                        </div>
                                        <Badge className="bg-emerald-600 text-white">Active</Badge>
                                    </div>
                                    {settings.system_calendar.connected_at && (
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <CalendarIcon className="h-3 w-3" />
                                            Connected {formatDateTime(settings.system_calendar.connected_at)}
                                        </div>
                                    )}
                                    <Button
                                        variant="outline"
                                        onClick={disconnectSystemCalendar}
                                        disabled={!canEdit}
                                        data-testid={ADMIN_SETTINGS.disconnect_calendar}
                                    >
                                        <Unlink className="mr-2 h-4 w-4" /> Disconnect
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                                        <AlertCircle className="h-5 w-5 text-amber-600" />
                                        <div className="text-sm text-amber-900">
                                            No system calendar connected. Meet links will only be created for mentors who have connected their own Google Calendar.
                                        </div>
                                    </div>
                                    <Button
                                        onClick={connectSystemCalendar}
                                        disabled={!canEdit}
                                        data-testid={ADMIN_SETTINGS.connect_calendar}
                                    >
                                        <Link2 className="mr-2 h-4 w-4" /> Connect Google Calendar
                                    </Button>
                                    <p className="text-xs text-muted-foreground">Use a dedicated Google Workspace account for this. All bookings without a mentor calendar connection will create events on this calendar with the student and mentor as attendees.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Danger zone — super admin only */}
            {!loading && isSuperAdmin && (
                <Card className="mt-6 border-destructive/30">
                    <CardContent className="space-y-4 p-6">
                        <div className="flex items-start gap-3">
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                                <ShieldAlert className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="font-display text-lg font-semibold text-destructive">Danger zone</h2>
                                <p className="text-sm text-muted-foreground">Permanently delete data. This cannot be undone.</p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <div className="text-sm font-medium">Reset booking data</div>
                                <p className="text-xs text-muted-foreground">Deletes all bookings and all feedback (mentor &amp; candidate). Student accounts and credit balances are kept.</p>
                            </div>
                            <Button
                                variant="outline"
                                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => { setResetConfirm(''); setResetOpen(true); }}
                                data-testid="admin-reset-bookings-btn"
                            >
                                <Trash2 className="mr-2 h-4 w-4" /> Reset booking data
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Reset confirmation dialog */}
            <Dialog open={resetOpen} onOpenChange={(o) => !o && setResetOpen(false)}>
                <DialogContent className="sm:max-w-md" data-testid="admin-reset-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-destructive">Reset booking data?</DialogTitle>
                        <DialogDescription>
                            This permanently deletes <strong>all bookings</strong> and <strong>all feedback</strong>. Student accounts and credit balances are preserved. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="reset-confirm">Type <span className="font-mono font-semibold">RESET</span> to confirm</Label>
                        <Input
                            id="reset-confirm"
                            value={resetConfirm}
                            onChange={(e) => setResetConfirm(e.target.value)}
                            placeholder="RESET"
                            data-testid="admin-reset-confirm-input"
                            autoComplete="off"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setResetOpen(false)}>Cancel</Button>
                        <Button
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={resetConfirm !== 'RESET' || resetting}
                            onClick={resetBookings}
                            data-testid="admin-reset-confirm-btn"
                        >
                            {resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                            Delete all booking data
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
